<template>
    <div class="CardWrapper">
        <div class="LoaderWrapper"
             :class="{jsHidden:loaded}"
        >
            <v-progress-linear indeterminate color="primary"/>
        </div>

        <div class="LoadedWrapper">
            <!-- Training/Tutorial Card -->

            <TrainingCard v-if="cardHasData && type && type === 'training'"
                          :name="name"
                          :card-has-data="cardHasData"
                          :loaded="loaded"
                          :fields="fields"
                          :namespace="namespace"
                          :key="namespace"
            />

            <!-- Data Collection Card -->
            <ExpansionCard v-else-if="cardHasData && type && type === 'data-collection'"
                           :name="name"
                           :card-has-data="cardHasData"
                           :loaded="loaded"
                           :fields="fields"
                           :namespace="namespace"
                           :key="namespace"
            />

            <!-- Support Form Card -->
            <SupportCard v-else-if="cardHasData && type && type === 'support'"
                           :name="name"
                           :card-has-data="cardHasData"
                           :loaded="loaded"
                           :fields="fields"
                           :namespace="namespace"
                           :key="namespace"
            />

            <!-- Website Design Card -->
            <WebsiteDesign v-else-if="cardHasData && type && type === 'website-design-preview'"
                           :name="name"
                           :card-has-data="cardHasData"
                           :loaded="loaded"
                           :fields="fields"
                           :namespace="namespace"
                           :key="namespace"
            />

            <IntroductoryCard v-else-if="cardHasData && type && type === 'introductory'"
                              :name="name"
                              :card-has-data="cardHasData"
                              :loaded="loaded"
                              :fields="fields"
                              :namespace="namespace"
                              :key="namespace"
            />

            <FreedomFigureEntryCard v-else-if="cardHasData && type && type === 'freedom-figure-entry'"
                              :name="name"
                              :card-has-data="cardHasData"
                              :loaded="loaded"
                              :fields="fields"
                              :namespace="namespace"
                              :key="namespace"
            />

            <FreedomFigureMonthlyNumbersCard v-else-if="cardHasData && type && type === 'freedom-figure-monthly-numbers' +
             ''"
                               :name="name"
                               :card-has-data="cardHasData"
                               :loaded="loaded"
                               :fields="fields"
                               :namespace="namespace"
                               :key="namespace"
            />

            <!--Default Card -->
            <v-card :key="namespace" v-else>
                <v-card-title v-if="cardHasData">
                    <v-spacer></v-spacer>
                    <HelpModal v-if="helpText"
                               :text="helpText"
                               :title="helpTitle"
                    ></HelpModal>
                </v-card-title>
                <v-card-text v-if="cardHasData">
                    <div v-for="(field, id) in fields"
                         :key="field.id+id"
                    >
                        <Field v-if="field.id"
                               :field-id="field.id"
                               :card-namespace="namespace"
                               :key="namespace+field.id+id"
                        />
                    </div>
                </v-card-text>
            </v-card>
        </div>
    </div>
</template>

<script>
import CardModule from '@/store/Modules/public/card.store';
import store from '../../../store/store';
import Field from '../Field/Field';
import { mapState } from 'vuex';
import ExpansionCard from './ExpansionCard';
import WebsiteDesign from '@/views/Public/Card/WebsiteDesign';
import TrainingCard from '@/views/Public/Card/TrainingCard';
import HelpModal from '@/components/HelpModal';
import SupportCard from '@/views/Public/Card/SupportCard';
import IntroductoryCard from '@/views/Public/Card/IntroductoryCard';
import FreedomFigureEntryCard from '@/views/Public/Card/FreedomFigureEntryCard';
import FreedomFigureMonthlyNumbersCard from '@/views/Public/Card/FreedomFigureMonthlyNumbersCard';

export default {
    components: { FreedomFigureMonthlyNumbersCard, FreedomFigureEntryCard, IntroductoryCard, SupportCard, HelpModal, TrainingCard, WebsiteDesign, ExpansionCard, Field },
    props: {
        id: {
            type: String,
            required: true
        }
    },

    computed: {
        ...mapState({
            state(state) {
                return state[this.namespace];
            },

            fieldsLoaded(state, getters) {
                return getters[this.namespace + '/loadedFields'];
            }
        }),

        type() {
            return store.state[this.namespace].type;
        },

        name() {
            return store.state[this.namespace].name;
        },

        helpTitle() {
            return store.state[this.namespace].helpTitle;
        },

        helpText() {
            return store.state[this.namespace].helpText;
        },

        fields() {
            return store.state[this.namespace].fields;
        },

        complete: {
            get: function() {
                return store.state[this.namespace].complete;
            },
            set: function(value) {
                store.commit(`${this.namespace}/setComplete`, value);
            }
        },

        helpModal: {
            get: function() {
                return store.state[this.namespace].helpModal;
            },
            set: function(value) {
                store.commit(`${this.namespace}/toggleHelpModal`, value);
            }
        }
    },

    watch: {
        cardHasData: {
            handler: function() {
                this.$watch('fieldsLoaded', () => {
                    this.checkLoadStatus();
                });
            },
            immediate: true
        }
    },

    data() {
        return {
            namespace: '',
            panel: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
            loaded: false,
            cardHasData: false
        };
    },

    created() {
        if (this.id) {
            this.registerStoreModule()
                .then((ns) => {
                    this.loadData(ns);
                });
        }
    },

    beforeDestroy() {
        store.unregisterModule(this.namespace);
    },

    methods: {
        registerStoreModule() {
            return new Promise((resolve) => {
                const ns = `card-user-${this.id}`;

                if ((store && store.state && store.state[ns])) {
                    store.unregisterModule(ns);
                }

                store.registerModule(ns, CardModule);
                resolve(ns);
            });
        },

        loadData(ns) {
            return new Promise(resolve => {
                store.dispatch(`${ns}/getData`, this.id)
                    .then(() => {
                        this.namespace = ns;
                        this.cardHasData = true;
                        resolve();
                    });
            });
        },

        setComplete() {
            this.complete = !this.complete;
            store.dispatch(`${this.namespace}/saveComplete`);
        },

        checkLoadStatus() {
            if(!this.loaded) {
                if (this.fields &&
                    this.fieldsLoaded &&
                    this.fields.length > 0 &&
                    this.fieldsLoaded.length > 0 &&
                    this.fields.length === this.fieldsLoaded.length ||
                    this.type === 'freedom-figure-entry' ||
                    this.type === 'freedom-figure-monthly-numbers'
                ) {
                    setTimeout(() => {
                        this.loaded = true;
                    }, 1000);
                }
            }
        },

        toggleHelpModal(value) {
            store.commit(`${this.namespace}/toggleHelpModal`, value);
        }
    }
};
</script>
<style lang="scss">
    .CardWrapper {
        position: relative;
        min-height: 66px;

        .v-card__title {
            padding-bottom: 0 !important;
        }
    }

    .CardTitle--big-center {
        display: flex;
        flex-flow: row wrap;
        justify-content: center;
        font-size: 2rem;
        font-weight: 900;
        margin-bottom: 24px;
        padding-top: 24px;
    }

    .LoadedWrapper {
        position: relative;
        z-index: 2;
    }

    .LoaderWrapper {
        position: absolute;
        z-index: 3;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: white;
        opacity: 1;
        transition: opacity 0.9s linear;

        &.jsHidden {
            opacity: 0;
            transition: opacity 0.9s linear;
            pointer-events: none;
        }
    }

    .TrainingCard {
        display: flex;
        flex-flow: row wrap;
        flex: 0 0 100%;
        width: 100%;
    }

    .TrainingCard__field-wrap {
        display: flex;
        flex-flow: row wrap;
        flex: 0 0 100%;
        width: 100%;
    }

    .TrainingGrid {
        display: grid;
        grid-template-columns: 1fr;
        grid-gap: 18px;
        list-style: none;
        margin: 0;
        padding: 0 !important;

        @media (min-width: 1280px) {
            grid-template-columns: 1fr 1fr 1fr;
            grid-gap: 48px;
        }
    }
</style>
